import React from 'react';
import { motion } from 'framer-motion';
import { User, Team, Task } from '../../types';
import {
  UsersIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';

interface StatsCardsProps {
  users: User[];
  teams: Team[];
  tasks: Task[];
  projects?: any[];
}

const StatsCards: React.FC<StatsCardsProps> = ({ users, teams, tasks, projects = [] }) => {
  const completedTasks = tasks.filter(task => task.status === 'DONE').length;
  const activeTasks = tasks.filter(task => task.status !== 'DONE' && task.status !== 'CANCELLED').length;
  const activeUsers = users.filter(user => user.isActive).length;
  const activeTeams = teams.filter(team => team.isActive).length;
  const activeProjects = projects.filter(project => project.status === 'ACTIVE').length;

  const stats = [
    {
      name: 'Total Users',
      value: activeUsers,
      total: users.length,
      icon: UsersIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Teams',
      value: activeTeams,
      total: teams.length,
      icon: UserGroupIcon,
      color: 'bg-green-500',
    },
    {
      name: 'Active Tasks',
      value: activeTasks,
      total: tasks.length,
      icon: ClipboardDocumentListIcon,
      color: 'bg-yellow-500',
    },
    {
      name: 'Active Projects',
      value: activeProjects,
      total: projects.length,
      icon: FolderIcon,
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => (
        <motion.div
          key={stat.name}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="card hover:shadow-md transition-shadow duration-200"
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`p-3 rounded-lg ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">of {stat.total}</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default StatsCards;
